import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";

const FAQS = [
  {
    q: "How does roster upload work?",
    a: "Copy and paste your roster straight in, or enter shifts manually. ROSTENO reads every shift, transition and commute, then builds your week around it. When the roster changes, your plan rebuilds automatically.",
  },
  {
    q: "Is my data private?",
    a: "Yes. Your roster and your plan are yours — encrypted in transit and at rest, never sold, never used for advertising. You can delete everything at any time.",
  },
  {
    q: "What kinds of rosters does it support?",
    a: "Rotating rosters, fixed shifts, casual and on-call work — days, afternoons, nights and the swings between them. If your week doesn't repeat, ROSTENO is built for you.",
  },
  {
    q: "Is ROSTENO medical advice?",
    a: "No. ROSTENO Coach offers general wellbeing guidance around sleep, training, nutrition and recovery — never medical advice. For medical or emergency issues, always contact a professional.",
  },
  {
    q: "When does ROSTENO launch?",
    a: "Waitlist members receive the first invitations, in order. Join the list and we'll write to you the moment your invite is ready.",
  },
];

function FaqItem({ item, index, open, onToggle }) {
  return (
    <div data-testid={`faq-item-${index + 1}`} className="border-t border-white/10">
      <button
        data-testid={`faq-question-${index + 1}`}
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-baseline gap-6 py-8 text-left group"
      >
        <span className="font-mono-accent text-xs tracking-[0.3em] text-[#8B99AE] shrink-0">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="font-serif-display font-medium text-2xl sm:text-3xl tracking-tight flex-1 transition-colors duration-300 group-hover:text-[#4ADE80]">
          {item.q}
        </span>
        <Plus
          className={`w-5 h-5 shrink-0 self-center text-[#8B99AE] transition-transform duration-300 ${
            open ? "rotate-45" : ""
          }`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p
              data-testid={`faq-answer-${index + 1}`}
              className="pb-8 pl-12 sm:pl-14 pr-8 text-sm sm:text-base leading-relaxed text-[#8B99AE] max-w-2xl"
            >
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <section
      data-testid="faq-section"
      className="border-t border-white/10 px-6 md:px-12 py-32 md:py-40"
    >
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="font-mono-accent text-xs sm:text-sm uppercase tracking-[0.3em] text-[#8B99AE] mb-16"
      >
        Questions, answered
      </motion.p>
      <div className="border-b border-white/10">
        {FAQS.map((item, i) => (
          <FaqItem
            key={item.q}
            item={item}
            index={i}
            open={open === i}
            onToggle={() => setOpen(open === i ? -1 : i)}
          />
        ))}
      </div>
    </section>
  );
}
